import {
	ACMilanLogo,
	ArsenalLogo,
	ASMonacoLogo,
	BarcelonaLogo,
	GalatasarayLogo,
	getOverlappedLogoSizeIcon,
	OverlappedLogosIconSize,
	OverlappedLogosIconSizePrefix,
	type OverlappedLogosProps,
	overlappedLogosSizeIcons,
	useAppTheme,
	useOverlappedLogos
} from '@capsicum/common';
import { useMemo } from 'react';
import styled from 'styled-components';

import { Image } from '../../atoms/image/Image';
import { Skeleton, SkeletonItem } from '../../atoms/skeleton/Skeleton';
import { Text } from '../../atoms/text/Text';

export const overlappedDefaultLogos = [
	{
		enabled: true,
		partnerName: 'AC Milan',
		url: ACMilanLogo.src
	},
	{
		enabled: true,
		partnerName: 'FC Barcelona',
		url: BarcelonaLogo.src
	},
	{
		enabled: true,
		partnerName: 'Arsenal FC',
		url: ArsenalLogo.src
	},
	{
		enabled: true,
		partnerName: 'Galatasaray S.K.',
		url: GalatasarayLogo.src
	},
	{
		enabled: true,
		partnerName: 'AS Monaco',
		url: ASMonacoLogo.src
	}
];

const SkeletonLogo = ({ iconSize = OverlappedLogosIconSize.MEDIUM }: { iconSize: OverlappedLogosIconSize }) => {
	return (
		<Skeleton>
			<SkeletonItem
				circle
				height={`${overlappedLogosSizeIcons[iconSize]}px`}
				width={`${overlappedLogosSizeIcons[iconSize]}px`}
			/>
		</Skeleton>
	);
};

export const OverlappedLogos = ({
	iconSize = OverlappedLogosIconSize.MEDIUM,
	isLoading = false,
	limitedListAmount = 0,
	logos,
	showStaticLogos,
	text
}: OverlappedLogosProps) => {
	const { mode } = useAppTheme();
	const { isLimitedLogos, limitedLogos, remainingLogoVariant, remainingNumberOfLogos } = useOverlappedLogos({
		iconSize,
		limitedListAmount,
		logos: showStaticLogos ? overlappedDefaultLogos : logos?.length ? logos : overlappedDefaultLogos
	});

	const logoCards = useMemo(() => {
		if (!limitedLogos || isLoading) {
			return [
				<S.Wrapper key={0}>
					<SkeletonLogo iconSize={iconSize} />
				</S.Wrapper>,
				<S.Wrapper key={1}>
					<SkeletonLogo iconSize={iconSize} />
				</S.Wrapper>
			];
		}

		return limitedLogos?.map((logo, index) => (
			<S.Wrapper key={index}>
				<S.ListCardImage
					$enabled={logo.enabled}
					$index={index}
					alt={logo?.partnerName || ''}
					height={
						overlappedLogosSizeIcons[iconSize] || overlappedLogosSizeIcons[OverlappedLogosIconSize.MEDIUM]
					}
					key={`${logo.url}-${index}`}
					src={logo.url as string}
					width={
						overlappedLogosSizeIcons[iconSize] || overlappedLogosSizeIcons[OverlappedLogosIconSize.MEDIUM]
					}
				/>
				<S.WhiteDiv $iconSize={iconSize} $index={index}></S.WhiteDiv>
			</S.Wrapper>
		));
	}, [limitedLogos, isLoading]);

	return (
		<S.Container>
			<S.LogoCarrousel>
				{logoCards}
				{isLimitedLogos && !isLoading && (
					<S.Wrapper>
						<S.RemainingLogos
							$iconSize={iconSize}
							$index={limitedListAmount + 1}
							$isDarkMode={mode === 'dark'}
						>
							<Text color="main.enabled" variant={remainingLogoVariant}>
								{`+${remainingNumberOfLogos}`}
							</Text>
						</S.RemainingLogos>
					</S.Wrapper>
				)}
			</S.LogoCarrousel>
			{text && <S.StyledText>{text}</S.StyledText>}
		</S.Container>
	);
};

namespace S {
	const OVERLAP_THRESHOLD = 6;

	export const Container = styled.div`
		display: flex;
		gap: ${({ theme }) => theme.spacings.small};
		margin-right: ${OVERLAP_THRESHOLD}px;
	`;
	export const LogoCarrousel = styled.div`
		display: flex;
	`;
	export const Wrapper = styled.div`
		margin-right: -${OVERLAP_THRESHOLD}px;
		display: flex;
	`;
	export const ListCardImage = styled(Image)<{ $enabled?: boolean; $index?: number }>`
		opacity: ${({ $enabled }) => ($enabled ? '1' : '0.3')};
		z-index: ${({ $index }) => $index};
		border-radius: ${({ theme }) => theme.border.radius.circle};
	`;
	export const WhiteDiv = styled.div<{ $iconSize?: OverlappedLogosIconSize; $index: number }>`
		background-color: ${({ theme }) => theme.colors.white};
		height: ${({ $iconSize }) => getOverlappedLogoSizeIcon($iconSize)};
		width: ${({ $iconSize }) => getOverlappedLogoSizeIcon($iconSize)};
		border-radius: ${({ theme }) => theme.spacings.medium};
		margin-left: ${({ $iconSize }) => getOverlappedLogoSizeIcon($iconSize, OverlappedLogosIconSizePrefix.NEGATIVE)};
		z-index: ${({ $index }) => $index - 1};
	`;

	export const RemainingLogos = styled.div<{
		$iconSize?: OverlappedLogosIconSize;
		$index: number;
		$isDarkMode?: boolean;
	}>`
		z-index: ${({ $index }) => $index};
		background-color: ${({ $isDarkMode, theme }) =>
			$isDarkMode ? theme.colors.icons.onDark.disabled : theme.colors.icons.onBrand.disabled};
		border-radius: ${({ theme }) => theme.spacings.medium};
		display: flex;
		justify-content: center;
		align-items: center;
		height: ${({ $iconSize }) => getOverlappedLogoSizeIcon($iconSize)};
		width: ${({ $iconSize }) => getOverlappedLogoSizeIcon($iconSize)};
	`;
	export const StyledText = styled.div`
		justify-content: flex-start;
		align-items: center;
		display: flex;
		${({ theme }) => theme.fonts.text.caption1}
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
